import { get, remove, set } from 'lib/storage/versionedLocalStorageUtils'
import { toUtmParams } from 'lib/url/utmCodesUtils'
import config from 'constants/config'
import uuidV4 from 'lib/string/uuidV4Utils'

// Redux state
// Increment version when we change the state structure in an incompatible way.
const REDUX_CART_STATE_KEY = 'checkoutCartReduxState'
const REDUX_CART_STATE_VERSION = 15

const REDUX_FORM_STATE_KEY = 'checkoutFormReduxState'
const REDUX_FORM_STATE_VERSION = 7

const REDUX_PAYMENT_STATE_KEY = 'checkoutPaymentReduxState'
const REDUX_PAYMENT_STATE_VERSION = 8

const CHECKOUT_OBSERVABILITY_STATE_KEY = 'checkoutObservabilityState'
const CHECKOUT_OBSERVABILITY_STATE_VERSION = 1
const CHECKOUT_OBSERVABILITY_RETENTION_MS = 7 * 24 * 60 * 60 * 1000

export interface CheckoutObservabilityState {
  cartId: string
  checkoutAttemptId: string
  checkoutLineageId: string
  createdAt: number
}

export interface PendingCheckoutRestore {
  sourceCartId: string
  expectedCartId: string
  sourceCheckoutAttemptId?: string
  checkoutLineageId: string
  createdAt: number
}

export interface StoredCheckoutObservabilityState {
  activeAttempt?: CheckoutObservabilityState
  pendingRestore?: PendingCheckoutRestore
}

function getStoredCheckoutObservabilityState(): StoredCheckoutObservabilityState {
  return get<StoredCheckoutObservabilityState>(CHECKOUT_OBSERVABILITY_STATE_KEY, CHECKOUT_OBSERVABILITY_STATE_VERSION) ?? {}
}

function setStoredCheckoutObservabilityState(state: StoredCheckoutObservabilityState) {
  set(CHECKOUT_OBSERVABILITY_STATE_KEY, CHECKOUT_OBSERVABILITY_STATE_VERSION, state)
}

function hasExpired(createdAt: number) {
  return Date.now() - createdAt > CHECKOUT_OBSERVABILITY_RETENTION_MS
}

export function getCheckoutObservabilityState(): StoredCheckoutObservabilityState {
  const state = getStoredCheckoutObservabilityState()
  const activeAttempt = state.activeAttempt && !hasExpired(state.activeAttempt.createdAt) ? state.activeAttempt : undefined
  const pendingRestore = state.pendingRestore && !hasExpired(state.pendingRestore.createdAt) ? state.pendingRestore : undefined

  if (activeAttempt !== state.activeAttempt || pendingRestore !== state.pendingRestore) {
    setStoredCheckoutObservabilityState({ activeAttempt, pendingRestore })
  }

  return { activeAttempt, pendingRestore }
}

export function saveCheckoutObservabilityAttempt(attempt: CheckoutObservabilityState) {
  const { pendingRestore } = getCheckoutObservabilityState()
  setStoredCheckoutObservabilityState({ activeAttempt: attempt, pendingRestore })
}

export function beginPendingCheckoutRestore(sourceCartId: string, expectedCartId: string): PendingCheckoutRestore {
  const { activeAttempt } = getCheckoutObservabilityState()
  const pendingRestore: PendingCheckoutRestore = {
    sourceCartId,
    expectedCartId,
    sourceCheckoutAttemptId: activeAttempt?.cartId === sourceCartId ? activeAttempt.checkoutAttemptId : undefined,
    checkoutLineageId: activeAttempt?.cartId === sourceCartId ? activeAttempt.checkoutLineageId : uuidV4(),
    createdAt: Date.now(),
  }
  setStoredCheckoutObservabilityState({ activeAttempt, pendingRestore })
  return pendingRestore
}

export function getOrCreateCheckoutObservabilityAttempt(cartId: string, pendingRestore?: PendingCheckoutRestore): { attempt: CheckoutObservabilityState, isNew: boolean } {
  const { activeAttempt } = getCheckoutObservabilityState()
  if (activeAttempt?.cartId === cartId && !pendingRestore) {
    return { attempt: activeAttempt, isNew: false }
  }

  const attempt: CheckoutObservabilityState = {
    cartId,
    checkoutAttemptId: uuidV4(),
    checkoutLineageId: pendingRestore?.checkoutLineageId ?? uuidV4(),
    createdAt: Date.now(),
  }
  saveCheckoutObservabilityAttempt(attempt)
  return { attempt, isNew: true }
}

export function consumePendingCheckoutRestore(newCartId: string): PendingCheckoutRestore | undefined {
  const { activeAttempt, pendingRestore } = getCheckoutObservabilityState()
  if (!pendingRestore) return undefined

  // Discard markers created by an older implementation that did not bind the
  // restore to its replacement cart. They cannot be linked safely.
  if (!pendingRestore.expectedCartId) {
    setStoredCheckoutObservabilityState({ activeAttempt, pendingRestore: undefined })
    return undefined
  }

  // A different checkout may mount before the restored cart (for example
  // after a tab is reopened). Keep the marker until its intended cart mounts.
  if (pendingRestore.expectedCartId !== newCartId) return undefined

  setStoredCheckoutObservabilityState({ activeAttempt, pendingRestore: undefined })
  return pendingRestore
}

export function clearCheckoutObservabilityState() {
  remove(CHECKOUT_OBSERVABILITY_STATE_KEY)
}

export function saveCheckoutReduxPaymentState(state: App.Checkout.PaymentState) {
  set(REDUX_PAYMENT_STATE_KEY, REDUX_PAYMENT_STATE_VERSION, state)
}

export function getSavedCheckoutReduxPaymentState(): App.Checkout.PaymentState | null {
  return get(REDUX_PAYMENT_STATE_KEY, REDUX_PAYMENT_STATE_VERSION)
}

export function saveCheckoutReduxCartState(state: App.CheckoutCartState) {
  set<{ state: App.CheckoutCartState, timestamp: number }>(REDUX_CART_STATE_KEY, REDUX_CART_STATE_VERSION, { state, timestamp: Date.now() })
}

export function getSavedCheckoutReduxCartState() {
  return get<{ state: App.SavedCheckoutReduxCartState, timestamp: number }>(REDUX_CART_STATE_KEY, REDUX_CART_STATE_VERSION)
}

export function clearSavedCheckoutReduxCartState() {
  remove(REDUX_CART_STATE_KEY)
}

export function saveCheckoutReduxFormState(
  cartId: string,
  formState: App.Checkout.FormState,
  formSchema: App.Checkout.TravellerFormSchemaData | undefined,
  memberId: string | undefined,
) {
  set(REDUX_FORM_STATE_KEY, REDUX_FORM_STATE_VERSION, { cartId, formState, formSchema, memberId, timestamp: Date.now() })
}

interface SavedCheckoutReduxFormState {
  cartId: string
  formState: App.Checkout.FormState
  formSchema: App.Checkout.TravellerFormSchemaData | undefined
  memberId: string | undefined
  timestamp: number
}

export function getSavedCheckoutReduxFormState(): SavedCheckoutReduxFormState | null {
  return get(REDUX_FORM_STATE_KEY, REDUX_FORM_STATE_VERSION)
}

export function clearSavedCheckoutReduxFormState() {
  remove(REDUX_FORM_STATE_KEY)
}

/**
 * svc-cart still expects UTM params
 *
 * @remarks
 * Customer portal no longer uses it, so we keep this implementation isolated to where still need it
 */
export function getUtmParams(utm: App.UTMState) {
  return {
    utm_source: config.BRAND,
    utm_medium: 'web',
    ...toUtmParams(utm),
  }
}
